import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import { Nullable } from "chat//imports/types";
import { getMessageFlag } from "chat/analytics/emitMessageActionEvent";
import { getReactionsTextAnalyticsParameter } from "chat/analytics/getReactionsTextAnalyticsParameter";
import {
  CHAT_ANALYTICS_PARAMS_CHAT_SOURCE_ID,
  CHAT_ANALYTICS_PARAMS_CLICK_ACTION,
  CHAT_ANALYTICS_PARAMS_EMOJI_TARGET,
  REACTIONS_LIST,
} from "chat/constants";
import { REACTION } from "chat/enums";
import { chatSelectors } from "chat/exports/state/selectors";
import { Typography } from "chat/imports/components";
import { HeroTypographyType } from "chat/imports/constants";
import { RootState, isSystemChat as getIsSystemChat } from "chat/imports/state";
import { sendMessageReaction } from "chat/state";
import { MessageIdentifier, TangoScreenState } from "chat/types";
import isGroupChatId from "chat/utils/isGroupChatId";
import styles from "./MessageReactionList.scss";

interface MessageReactionsListProps {
  accountId: string;
  closeMenu: VoidFunction;
  conversationId: Nullable<string>;
  message: MessageIdentifier;
  messageSenderId: string | undefined;
}

const selector =
  (conversationId: null | string, messageId: number) => (state: RootState) => ({
    myReactionId: chatSelectors.getMyMessageReaction(
      state,
      conversationId,
      messageId
    ),
    storedMessage: chatSelectors.getMessageById(
      state,
      conversationId,
      messageId
    ),
    messageReaction: chatSelectors.getMyMessageReaction(
      state,
      conversationId,
      messageId
    ),
    isFamilyChat: chatSelectors.getIsFamilyConversation(state, conversationId),
    isSystemChat: getIsSystemChat(state, conversationId || ""),
  });

export const MessageReactionsList = ({
  conversationId,
  closeMenu,
  message,
  messageSenderId,
  accountId,
}: MessageReactionsListProps) => {
  const dispatch = useDispatch();
  const {
    myReactionId,
    storedMessage,
    messageReaction,
    isFamilyChat,
    isSystemChat,
  } = useSelector(selector(conversationId, message.id));

  const isGroupChat = isGroupChatId(conversationId);

  const handleAnalyticsParams = useCallback(() => {
    const textAnalytics = getReactionsTextAnalyticsParameter({
      isGroupChat,
      isSystemChat,
      isFamilyChat,
    });

    return {
      target: CHAT_ANALYTICS_PARAMS_EMOJI_TARGET,
      action: CHAT_ANALYTICS_PARAMS_CLICK_ACTION,
      chatId: conversationId ?? "",
      flags: [getMessageFlag(storedMessage!)],
      tangoScreen: isGroupChat
        ? TangoScreenState.CHAT_GROUP
        : TangoScreenState.CHAT_SINGLE,
      messageId: storedMessage?.id.id,
      sourceId: CHAT_ANALYTICS_PARAMS_CHAT_SOURCE_ID,
      recipientAccountId: accountId ?? "",
      itemType: messageReaction,
      text: textAnalytics,
    };
  }, [
    conversationId,
    storedMessage,
    accountId,
    messageReaction,
    isGroupChat,
    isSystemChat,
    isFamilyChat,
  ]);

  const onReactionClickHandler = (reaction: REACTION) => () => {
    dispatch(
      sendMessageReaction({
        reactionId: reaction,
        deleteReaction: reaction === myReactionId,
        messageSenderId,
        message,
        analyticsParams: handleAnalyticsParams(),
      })
    );

    closeMenu();
  };

  return (
    <div className={styles.root}>
      {REACTIONS_LIST.map((reaction) => (
        <button
          key={reaction}
          className={classnames(styles.reaction, {
            [styles.mySelectedReaction]: reaction === myReactionId,
          })}
          onClick={onReactionClickHandler(reaction)}
        >
          <Typography
            className={styles.reaction}
            type={HeroTypographyType.HERO4}
          >
            {reaction}
          </Typography>
        </button>
      ))}
    </div>
  );
};
